/** @file clip.keyAll: keys every node (or some) at a frame with its current interpolated value. */
import type { NodeKey } from '@zilo/pose3d-format/types/clip';
import { checkArray } from '@zilo/pose3d-format/validate/fields/checkArray';
import { checkString } from '@zilo/pose3d-format/validate/fields/checkString';
import type { CommandDef } from '../apply/commandDef';
import { sampleNodeKey } from '../pose/sampleNodeKey';
import { findClip } from '../puppetDoc/findClip';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { writePose } from './writePose';

/** `clip.keyAll { clip, frame, nodes? }` → `{ frame, keyed }`: unkeyed nodes get `rot [0, 0, 0]`; merged into the key. */
export const clipKeyAllCommand: CommandDef<'clip.keyAll'> = {
  name: 'clip.keyAll',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['clip', 'frame', 'nodes']);
    return compact({ clip: r.str('clip'), frame: r.int('frame', { min: 0 }), nodes: r.opt('nodes', (k) => r.check(k, checkArray(checkString())) as string[]) });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input) => {
    const { name, clip } = findClip(draft, input.clip);
    const keyed = input.nodes ?? Object.keys(draft.puppet.nodes);
    const pose: Record<string, NodeKey> = Object.fromEntries(keyed.map((n) => {
      const s = sampleNodeKey(clip, n, input.frame);
      return [n, Object.keys(s).length === 0 ? { rot: [0, 0, 0] } : s];
    }));
    const out = writePose(draft, { clipName: name, frame: input.frame, nodes: pose, merge: true });
    return { ...out, result: { frame: input.frame, keyed } };
  },
};
